import React from 'react';
import {
  NEW_CARD_UID,
  PAYMENT_GATEWAYS,
  PAYMENT_METHODS,
  PAYMENT_METHOD_OPTIONS,
  type PaymentCardInput,
  type PaymentFormState,
  type PaymentInfo,
  type PaymentMethod
} from '../../types/payment';

type Props = {
  payment: PaymentInfo | null;
  value: PaymentFormState;
  onChange: (next: PaymentFormState) => void;
  disabled?: boolean;
};

const PaymentFormMessage = ({ payment, value, onChange, disabled = false }: Props) => {
  if (payment == null) return null;

  const { paymentMethodOptions, gatewayType, gatewayMembers } = payment;
  const showBilling =
    paymentMethodOptions === PAYMENT_METHOD_OPTIONS.INVOICE ||
    paymentMethodOptions === PAYMENT_METHOD_OPTIONS.SELECTABLE;
  const showCard =
    paymentMethodOptions === PAYMENT_METHOD_OPTIONS.CARD ||
    paymentMethodOptions === PAYMENT_METHOD_OPTIONS.SELECTABLE;
  const hasRegisteredCards = gatewayMembers.length > 0;
  const allowNewCard = gatewayType === PAYMENT_GATEWAYS.GMO;

  const updateMethod = (method: PaymentMethod) => {
    onChange({ ...value, paymentMethod: method });
  };

  const updateSelectedCard = (uid: string | typeof NEW_CARD_UID) => {
    onChange({ ...value, selectedCardUid: uid });
  };

  const updateCardInput = (field: keyof PaymentCardInput) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, cardInput: { ...value.cardInput, [field]: e.target.value } });
  };

  const updateCardNumber =
    (field: keyof PaymentCardInput, nextInputId: string | null) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
      onChange({ ...value, cardInput: { ...value.cardInput, [field]: digits } });
      if (digits.length === 4 && nextInputId) {
        document.getElementById(nextInputId)?.focus();
      }
    };

  const isNewCardSelected = value.paymentMethod === PAYMENT_METHODS.CARD && value.selectedCardUid === NEW_CARD_UID;

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>お支払い方法</h3>

      {showBilling && (
        <label style={styles.radioRow}>
          <input
            type="radio"
            name="payment-method"
            style={styles.radio}
            checked={value.paymentMethod === PAYMENT_METHODS.BILLING}
            onChange={() => updateMethod(PAYMENT_METHODS.BILLING)}
            disabled={disabled}
          />
          <span>請求書で支払う</span>
        </label>
      )}

      {showCard && (
        <>
          <label style={styles.radioRow}>
            <input
              type="radio"
              name="payment-method"
              style={styles.radio}
              checked={value.paymentMethod === PAYMENT_METHODS.CARD}
              onChange={() => updateMethod(PAYMENT_METHODS.CARD)}
              disabled={disabled}
            />
            <span>クレジットカードで決済する</span>
          </label>

          {value.paymentMethod === PAYMENT_METHODS.CARD && (
            <div style={styles.cardArea}>
              {gatewayType === PAYMENT_GATEWAYS.SONY && hasRegisteredCards && (
                <p style={styles.cardNote}>事前に登録されたクレジットカードで決済</p>
              )}

              {gatewayType !== PAYMENT_GATEWAYS.SONY &&
                gatewayMembers.map(member => (
                  <label key={member.uid} style={styles.cardRadioRow}>
                    <input
                      type="radio"
                      name="payment-card"
                      style={styles.radio}
                      checked={value.selectedCardUid === member.uid}
                      onChange={() => updateSelectedCard(member.uid)}
                      disabled={disabled}
                    />
                    <span>{member.name}</span>
                  </label>
                ))}

              {allowNewCard && (
                <label style={styles.cardRadioRow}>
                  <input
                    type="radio"
                    name="payment-card"
                    style={styles.radio}
                    checked={value.selectedCardUid === NEW_CARD_UID}
                    onChange={() => updateSelectedCard(NEW_CARD_UID)}
                    disabled={disabled}
                  />
                  <span>新しいカードで決済</span>
                </label>
              )}

              {/* Sony はカード未登録時に新規入力できない（管理者登録のみ） */}
              {!allowNewCard && !hasRegisteredCards && (
                <p style={styles.cardNote}>
                  ご利用可能なクレジットカードが登録されていません。組織の管理者にカード登録をご依頼ください。
                </p>
              )}

              {isNewCardSelected && (
                <div style={styles.newCardForm}>
                  <div style={styles.field}>
                    <label htmlFor="payment-card-number-1" style={styles.label}>
                      カード番号
                    </label>
                    <div style={styles.cardNumberRow}>
                      <input
                        id="payment-card-number-1"
                        style={styles.cardNumberInput}
                        type="text"
                        inputMode="numeric"
                        placeholder="0000"
                        maxLength={4}
                        value={value.cardInput.cardNumber1}
                        onChange={updateCardNumber('cardNumber1', 'payment-card-number-2')}
                        disabled={disabled}
                      />
                      <span style={styles.cardNumberSep}>-</span>
                      <input
                        id="payment-card-number-2"
                        aria-label="カード番号2"
                        style={styles.cardNumberInput}
                        type="text"
                        inputMode="numeric"
                        placeholder="0000"
                        maxLength={4}
                        value={value.cardInput.cardNumber2}
                        onChange={updateCardNumber('cardNumber2', 'payment-card-number-3')}
                        disabled={disabled}
                      />
                      <span style={styles.cardNumberSep}>-</span>
                      <input
                        id="payment-card-number-3"
                        aria-label="カード番号3"
                        style={styles.cardNumberInput}
                        type="text"
                        inputMode="numeric"
                        placeholder="0000"
                        maxLength={4}
                        value={value.cardInput.cardNumber3}
                        onChange={updateCardNumber('cardNumber3', 'payment-card-number-4')}
                        disabled={disabled}
                      />
                      <span style={styles.cardNumberSep}>-</span>
                      <input
                        id="payment-card-number-4"
                        aria-label="カード番号4"
                        style={styles.cardNumberInput}
                        type="text"
                        inputMode="numeric"
                        placeholder="0000"
                        maxLength={4}
                        value={value.cardInput.cardNumber4}
                        onChange={updateCardNumber('cardNumber4', null)}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                  <div style={styles.field}>
                    <label htmlFor="payment-card-expire-month" style={styles.label}>
                      有効期限
                    </label>
                    <div style={styles.expireRow}>
                      <input
                        id="payment-card-expire-month"
                        style={styles.expireInput}
                        type="text"
                        inputMode="numeric"
                        placeholder="MM"
                        maxLength={2}
                        value={value.cardInput.expireMonth}
                        onChange={updateCardInput('expireMonth')}
                        disabled={disabled}
                      />
                      <span>/</span>
                      <input
                        aria-label="有効期限（年）"
                        style={styles.expireInput}
                        type="text"
                        inputMode="numeric"
                        placeholder="YY"
                        maxLength={2}
                        value={value.cardInput.expireYear}
                        onChange={updateCardInput('expireYear')}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                  <div style={styles.field}>
                    <label htmlFor="payment-card-holder" style={styles.label}>
                      カード名義
                    </label>
                    <input
                      id="payment-card-holder"
                      style={styles.input}
                      type="text"
                      placeholder="ICHIRO YAMADA"
                      value={value.cardInput.holderName}
                      onChange={updateCardInput('holderName')}
                      disabled={disabled}
                    />
                  </div>
                  <div style={styles.field}>
                    <label htmlFor="payment-card-cvc" style={styles.label}>
                      セキュリティコード
                    </label>
                    <input
                      id="payment-card-cvc"
                      style={styles.input}
                      type="text"
                      inputMode="numeric"
                      placeholder="000"
                      maxLength={4}
                      value={value.cardInput.securityCode}
                      onChange={updateCardInput('securityCode')}
                      disabled={disabled}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <p style={styles.note}>※ 予約時に決済となります。</p>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    maxWidth: '360px',
    boxSizing: 'border-box' as const
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 12px',
    paddingBottom: '12px',
    borderBottom: '1px solid #e0e0e0'
  },
  radioRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer'
  },
  radio: {
    width: '16px',
    height: '16px',
    accentColor: '#1976d2',
    cursor: 'pointer',
    margin: 0,
    flex: '0 0 auto'
  },
  cardArea: {
    margin: '0 0 8px 24px',
    padding: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '4px'
  },
  cardRadioRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer'
  },
  newCardForm: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #ececec'
  },
  field: {
    marginBottom: '12px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    marginBottom: '4px',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box' as const
  },
  cardNumberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  },
  cardNumberInput: {
    width: '52px',
    padding: '8px 6px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    textAlign: 'center' as const,
    boxSizing: 'border-box' as const
  },
  cardNumberSep: {
    color: '#888'
  },
  expireRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  expireInput: {
    width: '60px',
    padding: '8px 10px',
    fontSize: '14px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    textAlign: 'center' as const
  },
  note: {
    fontSize: '12px',
    color: '#888',
    margin: '12px 0 0',
    paddingTop: '12px',
    borderTop: '1px solid #e0e0e0'
  },
  cardNote: {
    fontSize: '12px',
    color: '#888',
    margin: 0,
    lineHeight: '1.6'
  }
};

export default PaymentFormMessage;
